// client/src/pages/ApplyPage.jsx
// Landing page for new schools applying to open a TicTac account.
// Reached via the "פתח חשבון חדש" button on LoginScreen, which kicks
// off /auth/google?returnTo=/apply — so by the time we render, the
// user has a verified Google identity (req.user on the server). The
// form collects what Nedarim Plus needs to evaluate the lead:
//   • School logo (file upload, image/*, ≤5 MB)
//   • Approximate student count (free text — "כ-200" / "150-180" ok)
//   • Nedarim Plus institution number, with an explicit "אין מוסד" toggle
//   • Contact name + phone
// Submit posts multipart/form-data to /api/applications. Server emails
// the application to office@nedar.im via SES and returns 201.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api.js';
import { useLogout } from '../hooks/useMe.js';
import LogoWordmark from '../components/LogoWordmark.jsx';
import VersionFooter from '../components/VersionFooter.jsx';
import {
  CreditCardIcon,
  AcademicCapIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
} from '../components/Icons.jsx';

export default function ApplyPage({ user }) {
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [topError, setTopError] = useState(null);
  const [errors, setErrors] = useState({});

  // Form state
  const [schoolName, setSchoolName] = useState('');
  const [studentCount, setStudentCount] = useState('');
  const [hasInst, setHasInst] = useState(true);
  const [nedarimId, setNedarimId] = useState('');
  const [contactName, setContactName] = useState(user?.name || '');
  const [contactPhone, setContactPhone] = useState('');
  const [logoFile, setLogoFile] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setTopError(null);
    setErrors({});
    try {
      const fd = new FormData();
      fd.append('schoolName', schoolName.trim());
      fd.append('studentCount', studentCount.trim());
      fd.append('hasNedarimInstitution', String(hasInst));
      fd.append('nedarimInstitutionId', hasInst ? nedarimId.trim() : '');
      fd.append('contactName', contactName.trim());
      fd.append('contactPhone', contactPhone.trim());
      if (logoFile) fd.append('logo', logoFile);

      const res = await fetch('/api/applications', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      });
      const text = await res.text();
      let payload = null;
      try { payload = text ? JSON.parse(text) : null; } catch { /* swallow */ }
      if (!res.ok) {
        // ApiError takes an OBJECT (see client/src/lib/api.js) — earlier
        // call site passed positional args which all destructured to
        // undefined, so every server error rendered as "HTTP undefined"
        // and the dedicated send-failed branch below was unreachable.
        throw new ApiError({
          status:  res.status,
          code:    payload?.error,
          message: payload?.message,
          details: payload?.details,
        });
      }
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'logo_too_large') setTopError('הלוגו גדול מדי (עד 5MB).');
        else if (err.code === 'logo_invalid_type') setTopError('סוג הקובץ של הלוגו לא נתמך. השתמש ב-PNG / JPG / SVG.');
        else if (err.status === 422 && Array.isArray(err.details)) {
          const m = {};
          for (const i of err.details) m[i.path] = i.message;
          setErrors(m);
          setTopError('יש לתקן את השדות המסומנים.');
        } else if (err.status === 429) {
          setTopError('יותר מדי בקשות. נסה שוב בעוד מספר דקות.');
        } else if (err.code === 'email_provider_unavailable' || err.code === 'email_send_failed' || err.status === 502 || err.status === 503) {
          // Real failure path — server couldn't actually ship the email.
          // Must NOT fall through to the success screen. Surface a
          // dedicated retry message + a direct email fallback so the
          // applicant has something concrete to do.
          setTopError(
            'הבקשה לא נשלחה — בעיית מערכת בשליחת המייל. אנא נסה שוב בעוד רגע, ' +
            'או שלח את הפרטים ישירות למייל office@nedar.im.'
          );
        } else {
          setTopError(`שגיאה: ${err.message || err.code || 'לא צפויה'}`);
        }
      } else {
        setTopError('שגיאה לא צפויה. בדוק חיבור אינטרנט ונסה שוב.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (submitted) return <SuccessScreen user={user} />;

  return (
    <div className="min-h-screen flex flex-col">
      <ApplyHeader user={user} />
      <main className="flex-1 px-4 py-8 sm:py-10">
        <div className="max-w-2xl mx-auto animate-fade-in">
          <div className="card-pop p-7 sm:p-9 relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-2 bg-brand-gradient" />
            <p className="eyebrow">פתיחת חשבון</p>
            <h1 className="h-page mt-1.5">בואו נכיר את בית הספר שלכם.</h1>
            <p className="mt-2 text-muted">
              נציג מנדרים פלוס יחזור אליכם תוך יום-יומיים עם הצעת מחיר וזמני
              הקמה. נשלח את הפרטים האלה ל-<span dir="ltr">office@nedar.im</span>.
            </p>

            {topError && (
              <div
                role="alert"
                className="mt-5 rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2 animate-slide-up"
              >
                <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{topError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <Field
                label="שם בית הספר"
                error={errors.schoolName}
                required
                Icon={BuildingLibraryIcon}
                tone="brand"
              >
                <input
                  type="text"
                  className="input"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  maxLength={120}
                  required
                />
              </Field>

              <Field
                label="לוגו בית הספר"
                hint="PNG / JPG / SVG · עד 5MB · יודפס על כרטיסי התלמידים"
                Icon={CreditCardIcon}
                tone="gold"
              >
                <LogoUpload file={logoFile} onChange={setLogoFile} />
              </Field>

              <Field
                label="מספר תלמידים (משוער)"
                error={errors.studentCount}
                required
                Icon={AcademicCapIcon}
                tone="mint"
                hint="טווח או בערך — לא חייב מספר מדויק"
              >
                <input
                  type="text"
                  inputMode="numeric"
                  className="input"
                  value={studentCount}
                  onChange={(e) => setStudentCount(e.target.value)}
                  maxLength={40}
                  required
                />
              </Field>

              <Field
                label="מוסד בנדרים פלוס"
                error={errors.nedarimInstitutionId}
                Icon={DevicePhoneMobileIcon}
                tone="sky"
              >
                {/* Two-tile picker replaces the old single checkbox.
                    "אין למוסד מספר נדרים פלוס" as a checkbox label was
                    negated and confusing — half the applicants didn't
                    know which state meant which. Tiles make the choice
                    binary and obvious. */}
                <div className="grid grid-cols-2 gap-2.5">
                  <InstChoice
                    selected={hasInst}
                    onClick={() => setHasInst(true)}
                    tone="sky"
                    title="כן, יש לנו"
                    subtitle="נזין את המספר"
                  />
                  <InstChoice
                    selected={!hasInst}
                    onClick={() => setHasInst(false)}
                    tone="slate"
                    title="אין עדיין"
                    subtitle="לא נורא"
                  />
                </div>
                {hasInst && (
                  <input
                    type="text"
                    className="input mt-3"
                    value={nedarimId}
                    onChange={(e) => setNedarimId(e.target.value)}
                    maxLength={40}
                    inputMode="numeric"
                    dir="ltr"
                    autoFocus
                  />
                )}
              </Field>

              <Field
                label="איש קשר"
                error={errors.contactName}
                required
                Icon={UserGroupIcon}
                tone="joy"
              >
                <input
                  type="text"
                  className="input"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  maxLength={80}
                  required
                />
              </Field>

              <Field
                label="טלפון"
                error={errors.contactPhone}
                required
                Icon={DevicePhoneMobileIcon}
                tone="clay"
              >
                <input
                  type="tel"
                  inputMode="tel"
                  className="input"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  maxLength={30}
                  dir="ltr"
                  required
                />
              </Field>

              <div className="pt-2 flex flex-wrap gap-2 justify-end">
                <button
                  type="submit"
                  className="btn-pop btn-lg"
                  disabled={busy}
                >
                  {busy ? 'שולח...' : 'שלח בקשה'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
      <VersionFooter />
    </div>
  );
}

const TONE_TILE = {
  brand: 'bg-brand-100 text-brand-700',
  mint:  'bg-mint-100  text-mint-700',
  gold:  'bg-gold-100  text-gold-700',
  sky:   'bg-sky-100   text-sky-700',
  joy:   'bg-joy-100   text-joy-700',
  clay:  'bg-clay-100  text-clay-700',
};

function Field({ label, error, hint, required, Icon, tone, children }) {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-2">
        {Icon && (
          <div className={`w-8 h-8 rounded-xl grid place-items-center shrink-0 ${TONE_TILE[tone] || ''}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
        <label className="label !mb-0 flex-1">
          {label} {required && <span className="text-joy-600">*</span>}
        </label>
      </div>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

const INST_TONE = {
  sky: {
    selected: 'border-sky-500 bg-sky-50 shadow-card',
    idle:     'border-ink-200 hover:border-sky-300 hover:bg-sky-50/40',
    strap:    'bg-sky-gradient',
    check:    'bg-sky-500',
    title:    'text-sky-900',
  },
  slate: {
    selected: 'border-ink-400 bg-ink-50 shadow-card',
    idle:     'border-ink-200 hover:border-ink-300 hover:bg-ink-50/60',
    strap:    'bg-gradient-to-l from-ink-400 to-ink-600',
    check:    'bg-ink-500',
    title:    'text-ink-900',
  },
};

function InstChoice({ selected, onClick, tone, title, subtitle }) {
  const cls = INST_TONE[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`relative rounded-2xl border-2 p-3.5 text-start overflow-hidden transition-all
        ${selected ? cls.selected : cls.idle}`}
    >
      <div className={`absolute inset-x-0 top-0 h-1 ${cls.strap}`} />
      {selected && (
        <div
          className={`absolute top-2 end-2 w-5 h-5 rounded-full grid place-items-center ${cls.check}`}
          aria-hidden="true"
        >
          <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12l5 5 9-13" />
          </svg>
        </div>
      )}
      <div className={`font-bold text-base ${selected ? cls.title : 'text-ink-900'}`}>
        {title}
      </div>
      <div className="mt-0.5 text-xs text-ink-500">{subtitle}</div>
    </button>
  );
}

function LogoUpload({ file, onChange }) {
  return (
    <label
      className={`block rounded-2xl border-2 border-dashed transition-colors cursor-pointer
        ${file
          ? 'border-mint-400 bg-mint-50'
          : 'border-ink-300 hover:border-brand-400 hover:bg-brand-50/40'}`}
    >
      <input
        type="file"
        accept="image/*"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="sr-only"
      />
      <div className="p-5 text-center">
        {file ? (
          <>
            <div className="text-sm font-semibold text-ink-900 break-all">{file.name}</div>
            <div className="text-xs text-ink-500 num mt-0.5">
              {Math.round(file.size / 1024)} KB · לחץ להחלפת קובץ
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-semibold text-ink-900">בחר קובץ לוגו</div>
            <div className="text-xs text-ink-500 mt-0.5">או גרור לכאן</div>
          </>
        )}
      </div>
    </label>
  );
}

function ApplyHeader({ user }) {
  const logout = useLogout();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-ink-200/70">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label="TicTac · עמוד הבית"
          className="block shrink-0"
        >
          <LogoWordmark className="h-10 w-auto" />
        </button>
        <div className="ms-auto flex items-center gap-2">
          {user && (
            <span className="text-xs text-ink-500 hidden sm:inline" dir="ltr">{user.email}</span>
          )}
          <button
            type="button"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="btn-secondary btn-sm"
          >
            התנתק
          </button>
        </div>
      </div>
    </header>
  );
}

function SuccessScreen({ user }) {
  return (
    <div className="min-h-screen flex flex-col">
      <ApplyHeader user={user} />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="card-pop p-8 sm:p-10 max-w-xl w-full text-center relative overflow-hidden animate-fade-in">
          <div className="absolute inset-x-0 top-0 h-2 bg-mint-gradient" />
          <div className="mx-auto w-20 h-20 rounded-3xl bg-mint-gradient text-white grid place-items-center mb-4 shadow-pop">
            <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <h1 className="h-page">תודה, הבקשה התקבלה!</h1>
          <p className="mt-3 text-muted">
            נציג מנדרים פלוס יחזור אליכם תוך יום-יומיים. אם בינתיים יש שאלות —
            כתבו לנו ל-{' '}
            <a
              href="mailto:office@nedar.im"
              dir="ltr"
              className="font-bold text-brand-700 hover:text-brand-900 underline decoration-brand-300 decoration-2 underline-offset-4"
            >
              office@nedar.im
            </a>
            .
          </p>
        </div>
      </main>
      <VersionFooter />
    </div>
  );
}
